import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useRouteProtection } from '@/hooks/useRouteProtection';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import type {FeatureConfigKey} from '@/models';

interface ProtectedRouteProps {
  children: ReactElement;
  requireAdmin?: boolean;
  requireEmployee?: boolean;
  requiredFeature?: FeatureConfigKey;
  fallback?: string;
  loadingMessage?: string;
}

export default function ProtectedRoute({
  children,
  requireAdmin = false,
  requireEmployee = false,
  requiredFeature,
  fallback = "/dashboard",
  loadingMessage
}: ProtectedRouteProps) {
  const { hasAccess, loading, isFeatureLocked } = useRouteProtection(requireAdmin, requiredFeature, requireEmployee);

  if (loading) {
    return <LoadingSpinner message={loadingMessage} />;
  }

  if (!hasAccess) {
    // If feature is locked, redirect to feature locked page
    if (isFeatureLocked && requiredFeature) {
      return <Navigate to={`/feature-locked/${requiredFeature}`} replace />;
    }
    // Otherwise redirect to fallback (e.g., dashboard for admin-only routes)
    return <Navigate to={fallback} replace />;
  }

  return children;
}